import { Experience } from "../../../../context/dataExperienceContext";
import { PickupLocation, ProductDetail } from "../../../../context/viator-product-types";
import { BookingQuestion } from "../../../../types/bookingQuestions";

type TravelerPickup = Experience['product']['logistics']['travelerPickup'];

const arrivalAirQuestions = [
    'TRANSFER_AIR_ARRIVAL_AIRLINE',
    'TRANSFER_AIR_ARRIVAL_FLIGHT_NO',
    'TRANSFER_ARRIVAL_TIME',
    'TRANSFER_ARRIVAL_DROP_OFF',
    'PICKUP_POINT'
];

const arrivalRailQuestions = [
    'TRANSFER_RAIL_ARRIVAL_LINE',
    'TRANSFER_RAIL_ARRIVAL_STATION',
    'TRANSFER_ARRIVAL_TIME',
    'TRANSFER_ARRIVAL_DROP_OFF'
];

const arrivalSeaQuestions = [
    'TRANSFER_PORT_ARRIVAL_TIME',
    'TRANSFER_PORT_CRUISE_SHIP',
    'TRANSFER_ARRIVAL_DROP_OFF',
    'PICKUP_POINT'
];

const arrivalOtherQuestions = [
    'PICKUP_POINT'
];

const departureAirQuestions = [
    'TRANSFER_AIR_DEPARTURE_AIRLINE',
    'TRANSFER_AIR_DEPARTURE_FLIGHT_NO',
    'TRANSFER_DEPARTURE_DATE',
    'TRANSFER_DEPARTURE_TIME',
    'TRANSFER_DEPARTURE_PICKUP'
];

const departureRailQuestions = [
    'TRANSFER_RAIL_DEPARTURE_LINE',
    'TRANSFER_RAIL_DEPARTURE_STATION',
    'TRANSFER_DEPARTURE_DATE',
    'TRANSFER_DEPARTURE_TIME',
    'TRANSFER_DEPARTURE_PICKUP'
];

const departureSeaQuestions = [
    'TRANSFER_PORT_CRUISE_SHIP',
    'TRANSFER_DEPARTURE_DATE',
    'TRANSFER_PORT_DEPARTURE_TIME',
    'TRANSFER_DEPARTURE_PICKUP'
];

export const isConditionallyRequired = (questionId: string, arrivalMode: string, departureMode: string, travelerPickup: TravelerPickup, questions: BookingQuestion[]): boolean => {
    // If the question is in any of the mode-specific arrays and its corresponding mode question is not present,
    // it should be considered mandatory

    const allowCustomPickup = travelerPickup?.allowCustomTravelerPickup;
    const hasArrivalMode = questions.some(q => q.id === 'TRANSFER_ARRIVAL_MODE');
    const hasDepartureMode = questions.some(q => q.id === 'TRANSFER_DEPARTURE_MODE');

    const isInArrivalAir = arrivalAirQuestions.includes(questionId);
    const isInArrivalRail = arrivalRailQuestions.includes(questionId);
    const isInArrivalSea = arrivalSeaQuestions.includes(questionId);
    const isInArrivalOther = arrivalOtherQuestions.includes(questionId);

    const isInDepartureAir = departureAirQuestions.includes(questionId);
    const isInDepartureRail = departureRailQuestions.includes(questionId);
    const isInDepartureSea = departureSeaQuestions.includes(questionId);

    // Explained in section "Conditional booking questions". https://partnerresources.viator.com/travel-commerce/merchant/implementing-booking-questions/
    // If it's an arrival question and TRANSFER_ARRIVAL_MODE is not present, make it mandatory.
    // There is an special case for PICKUP_POINT.
    if (!hasArrivalMode && (isInArrivalAir || isInArrivalRail || isInArrivalSea || isInArrivalOther)) {
        /**
         * Rule applies only if logistics.travelerPickup.locations[] includes an item with pickupType: “AIRPORT”;
         * or, if logistics.travelerPickup.allowCustomTravelerPickup is true.
         * “PICKUP_POINT” answer must be a location for which pickupType is “AIRPORT”
         * if answer to “TRANSFER_ARRIVAL_MODE” is “AIR”.
         * https://partnerresources.viator.com/travel-commerce/merchant/implementing-booking-questions/
         */
        if (questionId === "PICKUP_POINT") {
            // Condition 1)
            if (isInArrivalAir) {
                if (hasPickupLocationsOfType(travelerPickup?.locations, "AIRPORT") || allowCustomPickup) {
                    return true;
                } else {
                    return false;
                }
            }
            // Condition 2)
            if (isInArrivalSea) {
                if (hasPickupLocationsOfType(travelerPickup?.locations, "PORT") || allowCustomPickup) {
                    return true;
                } else {
                    return false;
                }
            }

            // Condition 3)
            if (isInArrivalOther) {
                if (hasPickupLocationsOfType(travelerPickup?.locations, "HOTEL") || hasPickupLocationsOfType(travelerPickup?.locations, "LOCATION") || allowCustomPickup) {
                    return true;
                } else {
                    return false;
                }
            }

        } else {
            return true;
        }


        return true;
    }

    // If it's a departure question and TRANSFER_DEPARTURE_MODE is not present, make it mandatory
    if (!hasDepartureMode && (isInDepartureAir || isInDepartureRail || isInDepartureSea)) {
        return true;
    }


    if (arrivalMode === 'AIR') {
        if (isInArrivalAir) {
            if (questionId === "PICKUP_POINT") {
                if (hasPickupLocationsOfType(travelerPickup?.locations, "AIRPORT") || allowCustomPickup) {
                    return true;
                } else {
                    return false;
                }
            } else {
                return true
            }
        }
    }

    if (arrivalMode === 'RAIL') {
        if (isInArrivalRail) {
            return true;
        }
    }

    if (arrivalMode === 'SEA') {
        if (isInArrivalSea) {
            if (questionId === "PICKUP_POINT") {
                if (hasPickupLocationsOfType(travelerPickup?.locations, "PORT") || allowCustomPickup) {
                    return true;
                } else {
                    return false;
                }
            } else {
                return true
            }
        }
    }

    if (arrivalMode === 'OTHER') {
        if (isInArrivalAir) {
            if (questionId === "PICKUP_POINT") {
                if (
                    hasPickupLocationsOfType(travelerPickup?.locations, "HOTEL") ||
                    hasPickupLocationsOfType(travelerPickup?.locations, "LOCATION") || allowCustomPickup
                ) {
                    return true;
                } else {
                    return false;
                }
            } else {
                return true
            }
        }
    }

    if (departureMode === 'AIR') {
        if (isInDepartureAir) {
            return true;
        }
    }

    if (departureMode === 'RAIL') {
        if (isInDepartureRail) {
            return true;
        }
    }

    if (departureMode === 'SEA') {
        if (isInDepartureSea) {
            return true;
        }
    }

    return false;
};

export const shouldHideQuestion = (questionId: string, arrivalMode: string, departureMode: string): boolean => {
    // These is the set of questions that may be conditionally hidden.
    if (![
        ...arrivalAirQuestions,
        ...arrivalRailQuestions,
        ...arrivalSeaQuestions,
        ...arrivalOtherQuestions,
        ...departureAirQuestions,
        ...departureRailQuestions,
        ...departureSeaQuestions,
    ].includes(questionId)) {
        return false
    }

    const isInArrivalAir = arrivalAirQuestions.includes(questionId);
    const isInArrivalRail = arrivalRailQuestions.includes(questionId);
    const isInArrivalSea = arrivalSeaQuestions.includes(questionId);
    const isInArrivalOther = arrivalOtherQuestions.includes(questionId);

    const isInDepartureAir = departureAirQuestions.includes(questionId);
    const isInDepartureRail = departureRailQuestions.includes(questionId);
    const isInDepartureSea = departureSeaQuestions.includes(questionId);

    const isArrivalQuestion = isInArrivalAir || isInArrivalRail || isInArrivalSea || isInArrivalOther
    const isDepartureQuestion = isInDepartureAir || isInDepartureRail || isInDepartureSea;

    if(isArrivalQuestion) {
        if(arrivalMode === 'AIR') {
            if(isInArrivalAir) {
                return false
            } else {
                return true
            }
        }
        if(arrivalMode === 'RAIL') {
            if(isInArrivalRail) {
                return false
            } else {
                return true
            }
        }
        if(arrivalMode === 'SEA') {
            if(isInArrivalSea) {
                return false
            } else {
                return true
            }
        }
        if(arrivalMode === 'OTHER') {
            if(isInArrivalOther) {
                return false
            } else {
                return true
            }
        }
    }

    if(isDepartureQuestion) {
        if(departureMode === 'AIR') {
            if(isInDepartureAir) {
                return false
            } else {
                return true
            }
        }
        if(departureMode === 'RAIL') {
            if(isInDepartureRail) {
                return false
            } else {
                return true
            }
        }
        if(departureMode === 'SEA') {
            if(isInDepartureSea) {
                return false
            } else {
                return true
            }
        }
    }

    // If the question doesn't belong to either set, don't skip it
    return false;
};


/** From the documentation "Booking questions with allowed answers" section.
 *  https://docs.viator.com/partner-api/technical/#section/Booking-concepts.
 * These questions will help you identify arrival modes available for a product:
 * "AIR": "TRANSFER_AIR_ARRIVAL_AIRLINE", "TRANSFER_AIR_ARRIVAL_FLIGHT_NO"
 * "SEA": "TRANSFER_PORT_ARRIVAL_TIME" ("TRANSFER_PORT_CRUISE_SHIP" applies also to departure mode)
 * "RAIL": "TRANSFER_RAIL_ARRIVAL_LINE", "TRANSFER_RAIL_ARRIVAL_STATION"
 * If any of these questions is returned (even just one), the relevant option for arrival mode applies. Once an arrival mode is selected, travellers are required to answer all questions related to the selected arrival mode that are returned for the product in the bookingQuestions array (see the table below).
 */
export const getAvailableTransferModes = (questionId: string, questions: BookingQuestion[], travelerPickup: TravelerPickup) => {
    const modes = [];

    // This condition is mentioned in https://partnerresources.viator.com/travel-commerce/merchant/implementing-booking-questions/
    if (travelerPickup?.locations?.some(loc =>
        ['HOTEL', 'LOCATION', 'OTHER'].includes(loc.pickupType)
    )) {
        modes.push('OTHER');
    }

    if (questionId === "TRANSFER_ARRIVAL_MODE") {
        if (questions.some(q => [
            "TRANSFER_AIR_ARRIVAL_AIRLINE",
            "TRANSFER_AIR_ARRIVAL_FLIGHT_NO",
        ].includes(q.id))) {
            modes.push("AIR");
        }
        if (questions.some(q => [
            "TRANSFER_PORT_ARRIVAL_TIME",
            "TRANSFER_PORT_CRUISE_SHIP"

        ].includes(q.id))) {
            modes.push("SEA");
        }
        if (questions.some(q => [
            "TRANSFER_RAIL_ARRIVAL_LINE",
            "TRANSFER_RAIL_ARRIVAL_STATION",
        ].includes(q.id))) {
            modes.push("RAIL");
        }
    } else if (questionId === "TRANSFER_DEPARTURE_MODE") {
        if (questions.some(q => [
            'TRANSFER_AIR_DEPARTURE_AIRLINE',
            'TRANSFER_AIR_DEPARTURE_FLIGHT_NO',
        ].includes(q.id))) {
            modes.push("AIR");
        }
        if (questions.some(q => [
            'TRANSFER_PORT_DEPARTURE_TIME',
            'TRANSFER_PORT_CRUISE_SHIP'
        ].includes(q.id))) {
            modes.push("SEA");
        }
        if (questions.some(q => [
            'TRANSFER_RAIL_DEPARTURE_LINE',
            'TRANSFER_RAIL_DEPARTURE_STATION',
        ].includes(q.id))) {
            modes.push("RAIL");
        }
    }
    return modes;
};


export const getFilteredPickupLocations = (selectedArrivalMode: string, travelerPickup: TravelerPickup) => {
    if (!travelerPickup?.locations) return [];

    let locations = travelerPickup.locations;
    locations = travelerPickup.locations.filter(
        loc => (
            (loc.location.ref !== 'CONTACT_SUPPLIER_LATER') &&
            (loc.location.ref !== 'MEET_AT_DEPARTURE_POINT')
        )
    );

    switch (selectedArrivalMode) {
        case 'AIR':
            return locations.filter(loc => loc.pickupType === 'AIRPORT');
        case 'SEA':
            return locations.filter(loc => loc.pickupType === 'PORT');
        case 'OTHER':
            return locations.filter(loc =>
                ['HOTEL', 'LOCATION', 'OTHER'].includes(loc.pickupType)
            );
        default:
            return [];
    }
};

export const hasPickupLocationsOfType = (locations: PickupLocation[] | undefined, pickupType: PickupLocation['pickupType']): boolean => {
    return !!locations?.some(loc => loc.pickupType === pickupType);
};

export const isPickupOfferedByThisProductOption = (productOption: ProductDetail['productOptions'][0] | undefined, travelerPickup: TravelerPickup | undefined) => {
    // Es increible que esto funcione asi https://partnerresources.viator.com/travel-commerce/merchant/implementing-booking-questions/
    const productIncludesPickup =
        productOption?.description?.includes('Pickup included') ||
        productOption?.description?.includes('Recogida incluida')
    return (
      (travelerPickup?.pickupOptionType === 'PICKUP_EVERYONE') ||
      (travelerPickup?.pickupOptionType === 'PICKUP_AND_MEET_AT_START_POINT' && productIncludesPickup)
    )
  }